import { useDispatch } from 'react-redux';
import { graphql } from '~/graphql';
import { CART_QUERY_KEYS } from '~/utils/cartCache';
import { useGraphqlQuery } from '~/hooks/useGraphqlQuery';
import { useGraphqlMutation } from '~/hooks/useGraphqlMutation';
import { setUser } from '~/redux/reducers/auth';
import type { User } from '~/types';
import type { TradePerspective } from '~/graphql/graphql';
import { useEffect } from 'react';

export const useRefreshCurrentUser = () => {
  const currentUserQuery = graphql(`
    query currentUser {
      currentUser {
        externalId
        firstName
        id
        lastName
        role
        locations {
          id
          code
          name
          branchCode
          facility {
            id
            code
            address {
              addressLine1
              addressLine2
              city
              region
              zipCode
              country
            }
            transportVehicles {
              id
              name
              vehicleType
            }
          }
        }
        organization {
          id
          name
          defaultCurrency
          orderNotificationEmails {
            domestic
            international
          }
        }
      }
    }
  `);

  const resp = useGraphqlQuery(currentUserQuery, ['cur'], undefined, {
    staleTime: 0,
  });
  const user = resp?.data?.currentUser;

  const dispatch = useDispatch();
  useEffect(() => {
    if (!user) return;
    dispatch(setUser(user as User));
  }, [dispatch, user]);

  return resp;
};

export const useGetUserPreferences = () => {
  const userPreferences = graphql(`
    query getUserPreferences {
      userPreferences {
        category
        buyerInventorySelectedLocationId
        buyerInventorySelectedBranchCode
        sellerProductSelectedLocationId
        buyerInventoryColumnOrder
        buyerInventoryGroupedColumnOrder
        sellerInventoryColumnOrder
        sellerPricingColumnOrder
        pricesAndAvailabilityColumnOrder
        pricesAndAvailabilityGroupedColumnOrder
        sellerProductFilters {
          field
          operator
          value
          connector
        }
        buyerInventoryFilters {
          field
          operator
          value
          connector
        }
        buyerActiveFilterId
        sellerActiveFilterId
        buyerAnonymousFilter
        sellerAnonymousFilter
        buyerOrdersActiveFilterId
        sellerOrdersActiveFilterId
        buyerOrdersAnonymousFilter
        sellerOrdersAnonymousFilter
        buyerComparisonActiveFilterId
        buyerComparisonAnonymousFilter
        sellerPricingActiveFilterId
        sellerPricingAnonymousFilter
        productPreferredUnitFilters {
          category
          unit
        }
        buyListTeamMemberIds
        buyerBuyListsActiveFilterId
        buyListFilter
        myBuyListsActiveFilterId
        myBuyListFilter
        buyerRequestForQuotesActiveFilterId
        buyerRequestForQuotesAnonymousFilter
        randomTallyUseTallyPrice
      }
    }
  `);

  return useGraphqlQuery(userPreferences, ['userPreferences']);
};

export const useUpdateUserPreferences = () => {
  const updateUserPreferencesMutation = graphql(`
    mutation updateUserPreferences($preferences: UserPreferenceInput!) {
      updateUserPreferences(preferences: $preferences) {
        category
        buyerInventorySelectedLocationId
        buyerInventorySelectedBranchCode
        sellerProductSelectedLocationId
        buyerInventoryColumnOrder
        buyerInventoryGroupedColumnOrder
        sellerInventoryColumnOrder
        sellerPricingColumnOrder
        pricesAndAvailabilityColumnOrder
        pricesAndAvailabilityGroupedColumnOrder
        sellerProductFilters {
          field
          operator
          value
          connector
        }
        buyerInventoryFilters {
          field
          operator
          value
          connector
        }
        buyerActiveFilterId
        sellerActiveFilterId
        buyerAnonymousFilter
        sellerAnonymousFilter
        buyerOrdersActiveFilterId
        sellerOrdersActiveFilterId
        buyerOrdersAnonymousFilter
        sellerOrdersAnonymousFilter
        buyerComparisonActiveFilterId
        buyerComparisonAnonymousFilter
        sellerPricingActiveFilterId
        sellerPricingAnonymousFilter
        productPreferredUnitFilters {
          category
          unit
        }
        buyListTeamMemberIds
        buyerBuyListsActiveFilterId
        buyListFilter
        myBuyListsActiveFilterId
        myBuyListFilter
        buyerRequestForQuotesActiveFilterId
        buyerRequestForQuotesAnonymousFilter
        randomTallyUseTallyPrice
      }
    }
  `);

  return useGraphqlMutation(updateUserPreferencesMutation, ['userPreferences']);
};

export const useUpdateProductPreferredUnitFilters = () => {
  const mutation = graphql(`
    mutation updateProductPreferredUnitFilters(
      $filters: [ProductPreferredUnitFilterInput!]!
    ) {
      updateProductPreferredUnitFilters(filters: $filters) {
        productPreferredUnitFilters {
          category
          unit
        }
      }
    }
  `);

  return useGraphqlMutation(mutation, [
    'userPreferences',
    'buyerInventories',
    'uniqueProductsWithSellers',
    'orders',
    'my-products',
    ...CART_QUERY_KEYS,
    'sellerVendorInventoryGroupsForBuyer',
    'sellerProductInventories',
    'inventoryByProductAndLocation',
    'inventoriesByProductAndLocationForTally',
  ]);
};

export const useGetUserFilters = (perspective: TradePerspective) => {
  const userFiltersQuery = graphql(`
    query getUserFilters($perspective: TradePerspective!) {
      userFilters(perspective: $perspective) {
        id
        name
        filterData
        createdAt
      }
    }
  `);

  return useGraphqlQuery(userFiltersQuery, ['userFilters', perspective], {
    perspective,
  });
};

export const useSaveFilter = () => {
  const saveFilterMutation = graphql(`
    mutation saveFilter(
      $name: String!
      $filterData: JSON!
      $perspective: TradePerspective!
    ) {
      saveFilter(
        name: $name
        filterData: $filterData
        perspective: $perspective
      ) {
        id
        name
        filterData
        createdAt
      }
    }
  `);

  return useGraphqlMutation(saveFilterMutation, ['userFilters']);
};

export const useSetActiveFilter = () => {
  const setActiveFilterMutation = graphql(`
    mutation setActiveFilter(
      $filterId: ID!
      $perspective: TradePerspective!
      $view: FilterView
    ) {
      setActiveFilter(
        filterId: $filterId
        perspective: $perspective
        view: $view
      ) {
        buyerActiveFilterId
        sellerActiveFilterId
        buyerOrdersActiveFilterId
        sellerOrdersActiveFilterId
        buyerComparisonActiveFilterId
        sellerPricingActiveFilterId
        buyerBuyListsActiveFilterId
        myBuyListsActiveFilterId
        buyerRequestForQuotesActiveFilterId
      }
    }
  `);

  return useGraphqlMutation(setActiveFilterMutation, ['userPreferences']);
};

export const useClearActiveFilter = () => {
  const clearActiveFilterMutation = graphql(`
    mutation clearActiveFilter(
      $perspective: TradePerspective!
      $view: FilterView
    ) {
      clearActiveFilter(perspective: $perspective, view: $view) {
        buyerActiveFilterId
        sellerActiveFilterId
        buyerOrdersActiveFilterId
        sellerOrdersActiveFilterId
        buyerComparisonActiveFilterId
        sellerPricingActiveFilterId
        buyerBuyListsActiveFilterId
        myBuyListsActiveFilterId
        buyerRequestForQuotesActiveFilterId
      }
    }
  `);

  return useGraphqlMutation(clearActiveFilterMutation, ['userPreferences']);
};

export const useClearAnonymousFilter = () => {
  const clearAnonymousFilterMutation = graphql(`
    mutation clearAnonymousFilter(
      $perspective: TradePerspective!
      $view: FilterView
    ) {
      clearAnonymousFilter(perspective: $perspective, view: $view) {
        buyerAnonymousFilter
        sellerAnonymousFilter
        buyerActiveFilterId
        sellerActiveFilterId
        buyerOrdersAnonymousFilter
        sellerOrdersAnonymousFilter
        buyerOrdersActiveFilterId
        sellerOrdersActiveFilterId
        buyerComparisonAnonymousFilter
        buyerComparisonActiveFilterId
        sellerPricingActiveFilterId
        sellerPricingAnonymousFilter
        buyListFilter
        buyerBuyListsActiveFilterId
        myBuyListFilter
        myBuyListsActiveFilterId
        buyerRequestForQuotesAnonymousFilter
        buyerRequestForQuotesActiveFilterId
      }
    }
  `);

  return useGraphqlMutation(clearAnonymousFilterMutation, ['userPreferences']);
};

export const useRenameFilter = () => {
  const renameFilterMutation = graphql(`
    mutation renameFilter($id: ID!, $name: String!) {
      renameFilter(id: $id, name: $name) {
        id
        name
        filterData
        createdAt
      }
    }
  `);

  return useGraphqlMutation(renameFilterMutation, ['userFilters']);
};

export const useUpdateFilter = () => {
  const updateFilterMutation = graphql(`
    mutation updateFilter($id: ID!, $filterData: JSON!) {
      updateFilter(id: $id, filterData: $filterData) {
        id
        name
        filterData
        createdAt
      }
    }
  `);

  return useGraphqlMutation(updateFilterMutation, ['userFilters']);
};

export const useDeleteFilter = () => {
  const deleteFilterMutation = graphql(`
    mutation deleteFilter(
      $id: ID!
      $perspective: TradePerspective!
      $view: FilterView
    ) {
      deleteFilter(id: $id, perspective: $perspective, view: $view)
    }
  `);

  return useGraphqlMutation(deleteFilterMutation, [
    'userFilters',
    'userPreferences',
  ]);
};
